
import React from "react";
import { ColumnInfo } from "@/types/dbSchema";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface TableStructureViewerProps {
  tableColumns: ColumnInfo[];
  isLoadingStructure: boolean;
  expectedColumns: {
    column_name: string;
    data_type: string;
    is_nullable: string;
  }[];
}

export function TableStructureViewer({ 
  tableColumns, 
  isLoadingStructure,
  expectedColumns
}: TableStructureViewerProps) {
  if (isLoadingStructure) {
    return <p>Loading table structure...</p>;
  }

  if (tableColumns.length === 0) {
    return (
      <div>
        <p className="text-red-500">Unable to fetch table structure.</p>
        <p className="mt-2">Expected columns:</p>
        <ul className="list-disc list-inside mt-1">
          {expectedColumns.map(col => (
            <li key={col.column_name}>
              <code>{col.column_name}</code> ({col.data_type}, {col.is_nullable === 'NO' ? 'NOT NULL' : 'NULL'})
            </li>
          ))}
        </ul>
        <p className="mt-2 text-sm">
          Please check if the table exists and has the correct structure in Supabase.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableCaption>Current Supabase subscriptions table structure</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead>Column Name</TableHead>
            <TableHead>Data Type</TableHead>
            <TableHead>Nullable</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tableColumns.map((column) => {
            const expectedColumn = expectedColumns.find(
              (ec) => ec.column_name === column.column_name
            );
            
            const typeMatch = expectedColumn?.data_type === column.data_type;
            const nullableMatch = expectedColumn?.is_nullable === column.is_nullable;
            const hasMatch = !!expectedColumn;
            
            return (
              <TableRow key={column.column_name}>
                <TableCell>{column.column_name}</TableCell>
                <TableCell>{column.data_type}</TableCell>
                <TableCell>{column.is_nullable}</TableCell>
                <TableCell>
                  {hasMatch ? (
                    typeMatch && nullableMatch ? (
                      <span className="text-green-600">✓ Match</span>
                    ) : (
                      <span className="text-amber-500">
                        ⚠️ Type/Nullable mismatch
                      </span>
                    )
                  ) : (
                    <span className="text-blue-500">Extra column</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
          
          {/* Check for missing expected columns */}
          {expectedColumns.map((expected) => {
            const found = tableColumns.some(
              (tc) => tc.column_name === expected.column_name
            );
            
            if (!found) {
              return (
                <TableRow key={`missing-${expected.column_name}`} className="bg-red-50">
                  <TableCell>{expected.column_name}</TableCell>
                  <TableCell>{expected.data_type}</TableCell>
                  <TableCell>{expected.is_nullable}</TableCell>
                  <TableCell>
                    <span className="text-red-600">⚠️ Missing column</span>
                  </TableCell>
                </TableRow>
              );
            }
            return null;
          })}
        </TableBody>
      </Table>
    </div>
  );
}
